/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, skeletonStyles } from '@/styles';

@customElement('fg-collection-details-skeleton-loader')
export class FgCollectionDetailsSkeletonLoader extends LitElement {
  render() {
    return html`
      <div class="collection-details-skeleton">
        <div class="collection-details-header">
          <div class="skeleton-image"></div>
          <div class="skeleton-header-content">
            <div class="skeleton-title-large"></div>
            <div class="skeleton-text"></div>
          </div>
        </div>
        <section class="skeleton-settings">
          <div class="skeleton-legend"></div>
          <div class="skeleton-radio-group">
            <div class="skeleton-radio"></div>
            <div class="skeleton-radio"></div>
            <div class="skeleton-radio"></div>
          </div>
          <div class="skeleton-remarks">
            <div class="skeleton-remarks-item">
              <div class="skeleton-label"></div>
              <div class="skeleton-textarea"></div>
            </div>
            <div class="skeleton-remarks-item">
              <div class="skeleton-label"></div>
              <div class="skeleton-textarea"></div>
            </div>
          </div>
          <div class="skeleton-button-cta"></div>
        </section>
        <section class="skeleton-collection-contents">
          <div class="skeleton-table-header">
            <div
              class="skeleton-header-cell skeleton-header-cell--article"
            ></div>
            <div class="skeleton-header-cell skeleton-header-cell--price"></div>
            <div class="skeleton-header-cell skeleton-header-cell--price"></div>
          </div>
          <div class="skeleton-table-body">
            ${Array.from({ length: 2 }).map(
              () => html`
                <div class="skeleton-table-row">
                  <div class="skeleton-row-image"></div>
                  <div class="skeleton-row-text">
                    <div class="skeleton-item"></div>
                    <div class="skeleton-item"></div>
                  </div>
                  <div class="skeleton-item"></div>
                  <div class="skeleton-item"></div>
                  <div class="skeleton-delete-icon"></div>
                </div>
              `,
            )}
          </div>
          <div class="skeleton-pagination"></div>
        </section>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    skeletonStyles,
    css`
      .collection-details-skeleton .collection-details-header {
        display: grid;
        column-gap: 0.75rem;
        grid-template-columns: 92px 1fr;
        margin-bottom: 1.5rem;
      }

      .skeleton-image {
        aspect-ratio: 1;
        background: var(--skeleton-gray);
        border-radius: 46px;
        height: 92px;
        width: 92px;
      }

      .skeleton-header-content {
        align-items: flex-start;
        gap: 1rem;
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        padding: 0.75rem 0;
      }

      .skeleton-header-content .skeleton-title-large {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 2rem;
        width: 14rem;
      }

      .skeleton-remarks-item {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .skeleton-remarks-item .skeleton-label {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 10rem;
      }

      .skeleton-header-content .skeleton-text {
        height: 1rem;
        width: 10rem;
      }

      .skeleton-settings {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        padding: 1rem 1.25rem;
      }

      .skeleton-legend {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        margin-bottom: 1rem;
        width: 8rem;
      }

      .skeleton-radio-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }

      .skeleton-radio {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1.25rem;
        width: 6rem;
      }

      .skeleton-remarks {
        display: grid;
        gap: 0.5rem;
        grid-template-columns: 1fr;
        padding-top: 0.5rem;
        margin-bottom: 1rem;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .skeleton-radio-group {
          flex-direction: row;
        }

        .skeleton-remarks {
          grid-template-columns: 1fr 1fr;
        }
      }

      .skeleton-textarea {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 140px;
      }

      .skeleton-button-cta {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius-lg);
        height: 3rem;
        width: 14rem;
      }

      .skeleton-collection-contents {
        padding-top: 1.5rem;
      }

      .skeleton-table-header {
        display: none;
        gap: 0.5rem;
        grid-template-columns: 64px 1fr 190px 190px 64px;
        margin-bottom: 0.5rem;
        padding: 0 0.75rem 0.5rem;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .skeleton-table-header {
          display: grid;
        }
      }

      .skeleton-header-cell {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
      }

      .skeleton-header-cell--article {
        grid-column: span 2;
        width: 8rem;
      }

      .skeleton-header-cell--price {
        justify-self: end;
        width: 5rem;
      }

      .skeleton-table-body {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .skeleton-table-row {
        align-items: center;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        display: grid;
        gap: 0.5rem;
        grid-template-columns: 64px 1fr;
        padding: 0.75rem;
        position: relative;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .skeleton-table-row {
          grid-template-columns: 64px 1fr 190px 190px 64px;
        }
      }

      .skeleton-delete-icon {
        background: var(--skeleton-gray);
        border-radius: 50%;
        height: 24px;
        width: 24px;
        position: absolute;
        top: 0.75rem;
        right: 0.75rem;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .skeleton-delete-icon {
          position: relative;
          top: 0;
          right: 0;
          justify-self: end;
        }
      }

      .skeleton-row-image {
        aspect-ratio: 1;
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 64px;
        width: 64px;
      }

      .skeleton-row-text {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .skeleton-row-text .skeleton-item {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 0.875rem;
      }

      .skeleton-row-text .skeleton-item:first-child {
        width: 3rem;
      }

      .skeleton-row-text .skeleton-item:last-child {
        width: 10rem;
      }

      .skeleton-table-row > .skeleton-item {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        justify-self: end;
        width: 4rem;
      }

      .skeleton-pagination {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 2rem;
        margin-top: 1rem;
        width: 8rem;
        display: flex;
        justify-self: center;
      }
    `,
  ];
}
